import { useEffect, useMemo, useState } from 'react';
import { Header, Section, DataTable, Column, Badge, Button } from '../../shared/components';
import { getAuditRepository } from '../../shared/services/audit/repositoryFactory';
import { getSettingsRepository } from '../../shared/services/settings/repositoryFactory';
import { AuditFilters, AuditLogEntry } from '../../shared/services/audit/types';
import { ParamUser } from '../../shared/services/settings/types';
import { formatDateTime } from '../../shared/utils/datetime';
import { AuditFiltersBar } from './components/AuditFiltersBar';
import { AuditDetailModal } from './components/AuditDetailModal';

const auditRepo = getAuditRepository();
const settingsRepo = getSettingsRepository();

/**
 * Modulo: Auditoria. Bitacora de TODO lo modificado/eliminado en la plataforma
 * (hoy: Control de Tiempos). Cada fila guarda el registro COMPLETO tal como
 * estaba ANTES del cambio (ver Esq_Audit.TB_Audit_Log) - "Ver detalle" muestra
 * el antes/despues campo por campo.
 */
export function AuditPage() {
  const [users, setUsers] = useState<ParamUser[]>([]);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewing, setViewing] = useState<AuditLogEntry | null>(null);

  useEffect(() => {
    settingsRepo.listUsers(true).then(setUsers);
  }, []);

  // El input de fecha da "YYYY-MM-DD" local; se convierte a instante UTC real
  // antes de enviarlo (el Worker corre siempre en UTC, sin zona horaria local).
  const effectiveFilters = useMemo<AuditFilters>(
    () => ({
      ...filters,
      dateFrom: filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).toISOString() : undefined,
      dateTo: filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`).toISOString() : undefined,
    }),
    [filters]
  );

  useEffect(() => {
    setIsLoading(true);
    auditRepo.list(effectiveFilters, 1, 200).then((res) => {
      setEntries(res.items);
      setIsLoading(false);
    });
  }, [effectiveFilters]);

  const columns: Column<AuditLogEntry>[] = [
    { key: 'performedAt', header: 'Fecha', render: (e) => formatDateTime(e.performedAt), sortable: true, sortValue: (e) => e.performedAt },
    {
      key: 'action',
      header: 'Novedad',
      render: (e) => <Badge tone={e.action === 'DELETE' ? 'critical' : 'warning'}>{e.action === 'DELETE' ? 'Eliminado' : 'Modificado'}</Badge>,
    },
    { key: 'entityCode', header: 'Registro', render: (e) => <span className="font-mono text-xs text-ink-500">{e.entityCode ?? e.entityId}</span> },
    { key: 'performedBy', header: 'Realizado por', render: (e) => e.performedBy, sortable: true, sortValue: (e) => e.performedBy },
    { key: 'reason', header: 'Motivo', render: (e) => <span className="max-w-[240px] truncate text-ink-500" title={e.reason}>{e.reason}</span> },
    { key: 'detail', header: '', render: (e) => <Button variant="outline" size="md" onClick={() => setViewing(e)}>Ver detalle</Button> },
  ];

  return (
    <div>
      <Header title="Auditoría" subtitle="Historial de modificaciones y eliminaciones, con el registro tal como estaba antes" />
      <div className="space-y-6 p-4">
        <Section title="Filtros">
          <AuditFiltersBar value={filters} onChange={setFilters} users={users} />
        </Section>

        <DataTable
          columns={columns}
          rows={entries}
          rowKey={(e) => e.id}
          isLoading={isLoading}
          emptyTitle="Sin novedades registradas"
          emptyDescription="Aquí aparecerá cada vez que alguien modifique o elimine un registro de Tiempos."
          pageSize={10}
          defaultSortKey="performedAt"
          defaultSortDir="desc"
        />
      </div>

      <AuditDetailModal entry={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
